import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, ArrowLeft, Warning } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const validationError = useMemo(() => {
    if (!token) return 'Reset link is missing a token.';
    if (password && password.length < 8) return 'Password must be at least 8 characters.';
    if (password && confirm && password !== confirm) return 'Passwords do not match.';
    return null;
  }, [token, password, confirm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Reset link is missing or invalid. Please request a new one.');
      return;
    }
    if (password.length < 8) {
      toast.error('Please choose a password with at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: password });
      toast.success(data.message || 'Password updated. Please sign in.');
      navigate('/auth', { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reset your password. Please request a new link.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1521114978264-2223e08aa0d2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYmx1ZSUyMGFic3RyYWN0JTIwYXJjaGl0ZWN0dXJhbCUyMGxpbmVzfGVufDB8fHx8MTc4MDA3NzIwNHww&ixlib=rb-4.1.0&q=85)' }}
      >
        <div className="absolute inset-0 bg-[#0A1128]/80 flex items-center justify-center p-12">
          <div className="text-white max-w-lg">
            <h1 className="cabinet-grotesk text-4xl font-medium mb-2 text-[#C5A059]">PLANIVANCE AI</h1>
            <p className="text-xs uppercase tracking-[0.16em] text-[#C5A059]/70 mb-6">Client-Facing Financial Planning Platform</p>
            <p className="text-lg text-[#E2E8F0] leading-relaxed">
              Choose a new password. Then you're back in.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFCFB]">
        <div className="w-full max-w-md">
          <Link
            to="/auth"
            data-testid="reset-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#4A5568] hover:text-[#0A1128] mb-8 transition-colors"
          >
            <ArrowLeft size={16} /> Back to sign in
          </Link>

          <div className="mb-8">
            <h2 className="cabinet-grotesk text-3xl font-medium text-[#0A1128] mb-2">Set a new password</h2>
            <p className="text-[#4A5568]">Pick a strong password (8+ characters). You'll be redirected to sign in after.</p>
          </div>

          {!token && (
            <div data-testid="reset-missing-token" className="mb-5 bg-[#FFF5F5] border border-[#FED7D7] rounded-md p-4 flex items-start gap-3">
              <Warning size={20} weight="duotone" className="text-[#C53030] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-[#9B2C2C] font-medium">Reset link is missing.</p>
                <p className="text-xs text-[#9B2C2C] mt-1">
                  Please open the link from your reset email, or <Link to="/forgot-password" className="underline">request a new one</Link>.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
            <div>
              <Label htmlFor="password" className="text-sm font-medium text-[#1A202C] mb-2 block">New password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                <Input
                  id="password"
                  type="password"
                  data-testid="reset-password-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirm" className="text-sm font-medium text-[#1A202C] mb-2 block">Confirm password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5568]" size={20} />
                <Input
                  id="confirm"
                  type="password"
                  data-testid="reset-confirm-input"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-10 border-slate-300 bg-white text-slate-900 rounded-md focus:ring-2 focus:ring-[#C5A059]/50 focus:border-[#C5A059]"
                  required
                  minLength={8}
                />
              </div>
              {validationError && password && (
                <p data-testid="reset-validation-error" className="text-xs text-[#C53030] mt-2">{validationError}</p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="reset-submit-button"
              disabled={loading || !token || !password || !confirm || !!validationError}
              className="w-full bg-[#0A1128] text-white hover:bg-[#152145] rounded-md py-2.5 text-sm font-medium transition-colors"
            >
              {loading ? 'Updating...' : 'Update password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
